"use client";

import React, { useEffect, useRef, useState } from "react";

// The landing scene's particle canvas, extracted so other public pages can
// reuse it. 2D canvas with a hand-rolled projection: cheaper than a WebGL
// context per panel, and the look is the point rather than the geometry.

export type ParticleShape = "grid" | "sphere" | "cube" | "ring" | "text";

export const PARTICLE_COLORS = {
  primary: "#06b6d4",   // Cyan-500
  secondary: "#8b5cf6", // Violet-500
  accent: "#d946ef"     // Fuchsia-500
};

const CONFIG = {
  particleSize: 1.8,
  mouseRepulsion: 150,
  focalLength: 400,
  twistSpeed: 0.002
};

const isTouchDevice = () =>
  typeof window !== "undefined" && (window.innerWidth < 768 || "ontouchstart" in window);

type Particle = {
  x: number; y: number; z: number;
  tx: number; ty: number; tz: number;
  vx: number; vy: number; vz: number;
  color: string; baseColor: string;
};

type ParticleFieldProps = {
  shape: ParticleShape;
  // Only read for shape "text": the glyphs the swarm settles into.
  text?: string;
  // Multiplies the particle budget. Panel-sized canvases want less than the
  // full-bleed landing scene.
  density?: number;
  // Multiplies the fitted shape size. The fit is clamped so a shape stays
  // readable in a small panel, and that same clamp leaves it marooned in the
  // middle of a full-screen scene - which is what this opens up.
  scale?: number;
  // Trail colour as an "r, g, b" triple - the canvas is opaque, so it has to
  // match whatever it sits on.
  trail?: string;
  className?: string;
};

export default function ParticleField({
  shape,
  text = "",
  density = 1,
  scale = 1,
  trail = "2, 4, 10",
  className = "block w-full h-full"
}: ParticleFieldProps) {
  const isMobileRef = useRef(isTouchDevice());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const setTargetsRef = useRef<() => void>(() => {});

  const [particles, setParticles] = useState<Particle[]>([]);
  useEffect(() => {
    const count = Math.max(200, Math.round((isTouchDevice() ? 1600 : 4000) * density));
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setParticles(Array.from({ length: count }, () => {
      const rand = Math.random();
      // Mixed color palette to match Cestus theme
      const color = rand > 0.8 ? PARTICLE_COLORS.accent
        : rand > 0.4 ? PARTICLE_COLORS.primary
          : PARTICLE_COLORS.secondary;

      return {
        x: (Math.random() - 0.5) * 800,
        y: (Math.random() - 0.5) * 800,
        z: (Math.random() - 0.5) * 800,
        tx: 0, ty: 0, tz: 0,
        vx: 0, vy: 0, vz: 0,
        color,
        baseColor: color
      };
    }));
  }, [ density ]);

  useEffect(() => {
    let running = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    const total = particles.length;
    let width = 0, height = 0, cx = 0, cy = 0;
    let rotation = 0;

    const resize = () => {
      isMobileRef.current = isTouchDevice();
      width = canvas.parentElement?.offsetWidth || window.innerWidth;
      height = canvas.parentElement?.offsetHeight || window.innerHeight;

      const dpr = isMobileRef.current ? 1 : window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      cx = width / 2;
      cy = isMobileRef.current ? height * 0.4 : height / 2;
    };

    setTargetsRef.current = () => {
      if (width === 0 || height === 0) return;

      // 1. TEXT SHAPE
      if (shape === "text" && text) {
        const tCanvas = document.createElement("canvas");
        const tCtx = tCanvas.getContext("2d");
        if (!tCtx) return;
        tCanvas.width = width; tCanvas.height = height;
        tCtx.font = isMobileRef.current
          ? '900 56px "Courier New", monospace'
          : '900 120px "Courier New", monospace';
        tCtx.textAlign = "center"; tCtx.textBaseline = "middle";
        tCtx.fillText(
          text,
          width / 2,
          isMobileRef.current ? height * 0.48 : height / 2
        );

        const imgData = tCtx.getImageData(0, 0, width, height).data;
        const pixels: { x: number; y: number }[] = [];
        const step = isMobileRef.current ? 3 : 4;

        for (let y = 0; y < height; y += step) {
          for (let x = 0; x < width; x += step) {
            if (imgData[(y * width + x) * 4 + 3] > 128) pixels.push({ x: x - width / 2, y: y - height / 2 });
          }
        }

        particles.forEach((p, i) => {
          if (i < pixels.length) {
            p.tx = pixels[i].x;
            p.ty = pixels[i].y;
            p.tz = 0;
            p.color = Math.random() > 0.6 ? PARTICLE_COLORS.accent : PARTICLE_COLORS.primary;
          } else {
            p.tx = (Math.random() - 0.5) * width * 1.5;
            p.ty = (Math.random() - 0.5) * height * 1.5;
            p.tz = (Math.random() - 0.5) * 500;
            p.color = PARTICLE_COLORS.secondary;
          }
        });
      }

      // 2. SPHERE SHAPE
      else if (shape === "sphere") {
        const radius = Math.max(120, Math.min(250, Math.min(width, height) * 0.4)) * scale;
        particles.forEach((p, i) => {
          const phi = Math.acos(-1 + (2 * i) / total);
          const theta = Math.sqrt(total * Math.PI) * phi;
          p.tx = radius * Math.sin(phi) * Math.cos(theta);
          p.ty = radius * Math.sin(phi) * Math.sin(theta);
          p.tz = radius * Math.cos(phi);
          p.color = p.baseColor;
        });
      }

      // 3. CUBE SHAPE
      else if (shape === "cube") {
        const size = Math.max(180, Math.min(300, Math.min(width, height) * 0.55)) * scale;
        const half = size / 2;
        particles.forEach(p => {
          p.tx = (Math.random() - 0.5) * size;
          p.ty = (Math.random() - 0.5) * size;
          p.tz = (Math.random() - 0.5) * size;

          // Snap to faces for cleaner cube look
          if (Math.random() > 0.8) {
            const axis = Math.floor(Math.random() * 3);
            if (axis === 0) p.tx = Math.random() > 0.5 ? half : -half;
            if (axis === 1) p.ty = Math.random() > 0.5 ? half : -half;
            if (axis === 2) p.tz = Math.random() > 0.5 ? half : -half;
          }
          p.color = p.baseColor;
        });
      }

      // 4. RING SHAPE
      else if (shape === "ring") {
        const r1 = 180 * scale;
        const r2 = 60 * scale;
        particles.forEach((p, i) => {
          const u = (i / total) * Math.PI * 2 * 4;
          const v = (i / total) * Math.PI * 2;
          p.tx = (r1 + r2 * Math.cos(v)) * Math.cos(u);
          p.ty = (r1 + r2 * Math.cos(v)) * Math.sin(u);
          p.tz = r2 * Math.sin(v);
          p.color = p.baseColor;
        });
      }

      // 5. GRID/DEFAULT
      else {
        const size = (isMobileRef.current ? 400 : 800) * scale;
        const cols = isMobileRef.current ? 30 : 50;
        const spacing = size / cols;
        particles.forEach((p, i) => {
          const col = i % cols;
          const row = Math.floor(i / cols) % cols;
          p.tx = (col * spacing) - size / 2;
          p.ty = 200; // Floor plane
          p.tz = (row * spacing) - size / 2;
          p.color = Math.random() > 0.9 ? PARTICLE_COLORS.accent : PARTICLE_COLORS.secondary;
        });
      }
    };

    const render = () => {
      // Clear with trail effect
      ctx.fillStyle = `rgba(${trail}, ${isMobileRef.current ? 0.4 : 0.25})`;
      ctx.fillRect(0, 0, width, height);

      rotation += CONFIG.twistSpeed;
      const sinR = Math.sin(rotation);
      const cosR = Math.cos(rotation);

      particles.forEach(p => {
        const speed = isMobileRef.current ? 0.015 : 0.03;
        p.vx += (p.tx - p.x) * speed;
        p.vy += (p.ty - p.y) * speed;
        p.vz += (p.tz - p.z) * speed;

        p.vx *= 0.8;
        p.vy *= 0.8;
        p.vz *= 0.8;

        p.x += p.vx;
        p.y += p.vy;
        p.z += p.vz;

        const rx = p.x * cosR - p.z * sinR;
        const rz = p.z * cosR + p.x * sinR;

        const scale = CONFIG.focalLength / (CONFIG.focalLength + rz);
        const x2d = rx * scale + cx;
        const y2d = p.y * scale + cy;

        // Mouse Repulsion
        if (!isMobileRef.current) {
          const dx = mouseRef.current.x - x2d;
          const dy = mouseRef.current.y - y2d;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < CONFIG.mouseRepulsion && dist > 0.001) {
            const force = (CONFIG.mouseRepulsion - dist) / CONFIG.mouseRepulsion;
            p.vx -= (dx / dist) * force * 5;
            p.vy -= (dy / dist) * force * 5;
          }
        }

        if (scale > 0) {
          ctx.fillStyle = p.color;
          const size = Math.max(0, CONFIG.particleSize * scale);
          ctx.fillRect(x2d, y2d, size, size);
        }
      });

      if (running) requestAnimationFrame(render);
    };

    resize();
    setTargetsRef.current();
    window.addEventListener("resize", resize);

    const handleMouse = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    canvas.addEventListener("mousemove", handleMouse);

    requestAnimationFrame(render);

    return () => {
      running = false;
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", handleMouse);
    };
  }, [ shape, text, trail, scale, particles ]); // Re-run when shape/text changes

  useEffect(() => {
    // Immediate target update when props change
    setTargetsRef.current();
  }, [ shape, text, scale ]);

  return <canvas ref={canvasRef} className={className} />;
}
